import { Trans, useLingui } from '@lingui/react/macro';
import { useContext, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../contexts/ThemeContext';
import { accountService } from '../services/accountService';
import { useTier } from '../contexts/TierContext';
import MaxBadge from './MaxBadge';
import UpgradeModal from './UpgradeModal';

/** Matches privacypal-web desktop.html download URLs and copy. */
// Keep in step with lib/privacypal-proxy/package.json on every release: this
// drives the public download links, and a stale value serves new users an old
// build. It sat at 1.9.7 while the auto-update feed was on 1.9.19, so a fresh
// download was thirteen releases behind and missing two protection fixes.
//
// Windows and macOS are cut independently and are NOT always on the same
// version, so they are pinned separately.  One shared pin is what left the
// macOS button dead: the portal built
// ``proxy/macos/PrivacyPal-<shared version>-arm64.dmg`` while the macOS
// builds publish under ``proxy/macos/cloud/`` and that legacy root stopped
// receiving dmgs at 1.9.9 — so every value past 1.9.9 (it was on 1.9.44)
// pointed at an object that had never existed and the download 403'd.  Each
// link now carries the version that platform actually shipped, from the
// prefix that actually holds it.  Verify a bump with a HEAD request against
// the URL before deploying, not just against the auto-update feed.
const DESKTOP_VERSION_WINDOWS = '1.9.66';
const DESKTOP_VERSION_MACOS = '1.9.66';
const DESKTOP_RELEASES_BASE = 'https://privacypal-production-desktop-596719033801.s3.us-east-1.amazonaws.com';
const DOWNLOAD_WINDOWS_EXE = `${DESKTOP_RELEASES_BASE}/proxy/windows/PrivacyPal-Setup-${DESKTOP_VERSION_WINDOWS}.exe`;
const DOWNLOAD_MACOS_ARM64 = `${DESKTOP_RELEASES_BASE}/proxy/macos/cloud/PrivacyPal-${DESKTOP_VERSION_MACOS}-arm64.dmg`;
const CLI_WINGET = 'winget install PrivacyPal.AI';
const CLI_LINUX_INSTALL = 'tar -xzf privacypal-agent-*.tar.gz && cd privacypal-agent && ./install.sh';
//
// Linux is pinned separately again, and for a different reason than the
// Windows/macOS split above: it is not the same product. There is no Electron
// build for Linux and there never has been (EPIC-10, "CLI only"); what ships
// is the headless agent tarball, cut by hand from installers/linux and
// published under ``proxy/linux/cloud/``. Because it is cut by hand it
// routinely lags the GUI platforms, so a shared pin would point at a tarball
// that does not exist yet. Bump this only after `aws s3 ls` shows the objects.
const DESKTOP_VERSION_LINUX = '1.9.66';
const DOWNLOAD_LINUX_X86_64 = `${DESKTOP_RELEASES_BASE}/proxy/linux/cloud/privacypal-agent-${DESKTOP_VERSION_LINUX}-linux-x86_64.tar.gz`;
const DOWNLOAD_LINUX_AARCH64 = `${DESKTOP_RELEASES_BASE}/proxy/linux/cloud/privacypal-agent-${DESKTOP_VERSION_LINUX}-linux-aarch64.tar.gz`;

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const { t } = useLingui();
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const { toggleTheme, isDark } = useTheme();
  const { isMax } = useTier();
  const [company, setCompany] = useState<any>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [accountStatus, setAccountStatus] = useState<any>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (user) {
      loadCompanyInfo();
    }
  }, [user]);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setMobileMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);


  const loadCompanyInfo = async () => {
    try {
      const companyData = await accountService.getCompany();
      if (companyData) {
        setCompany(companyData);
      }
      const account = await accountService.getAccountStatus();
      setIsAdmin(account.isAdmin || false);
      setAccountStatus(account);
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Failed to load company info:', error);
      }
    }
  };

  const shouldShowSubscription = () => {
    if (!isAdmin) {
      const isPro = accountStatus?.status === 'pro';
      return !isPro;
    }
    return true;
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const handleGenerateInvite = async () => {
    // EPIC-15 (PLG): the share-with-teammate moment IS the upsell. Team
    // protection is Max; a free account gets the checkout, not an error.
    if (!isMax) {
      setUpgradeOpen(true);
      return;
    }
    try {
      const result = await accountService.generateInviteLink();
      setInviteLink(result.inviteUrl);
      setShowInviteModal(true);
    } catch (error: any) {
      console.error('Failed to generate invite link:', error);
      showToast(error.response?.data?.error || t`Failed to generate invite link. Please ensure you have a company account.`, 'error');
    }
  };

  const handleCopyInvite = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const copyCliCommand = (command: string) => {
    navigator.clipboard.writeText(command).then(() => {
      showToast(t`Copied to clipboard`, 'success');
    });
  };

  const navItems = (onNavigate?: () => void) => (
    <nav className="space-y-1">
      <button
        onClick={() => { navigate('/dashboard'); onNavigate?.(); }}
        className={`flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors ${
          isActive('/dashboard')
            ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-500/10 font-semibold'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5'
        }`}
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z" />
        </svg>
        <Trans>Activity Dashboard</Trans>
      </button>
      {shouldShowSubscription() && (
        <button
          onClick={() => { navigate('/subscription'); onNavigate?.(); }}
          className={`flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors ${
            isActive('/subscription')
              ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-500/10 font-semibold'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5'
          }`}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
          </svg>
          <Trans>Subscription</Trans>
        </button>
      )}
      <button
        onClick={() => { navigate('/agents'); onNavigate?.(); }}
        className={`flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors ${
          isActive('/agents')
            ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-500/10 font-semibold'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5'
        }`}
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714a2.25 2.25 0 00.659 1.591L19 14.5M14.25 3.104c.251.023.501.05.75.082M19 14.5l-1.47 4.41a2.25 2.25 0 01-2.133 1.59H8.603a2.25 2.25 0 01-2.134-1.59L5 14.5m14 0H5" />
        </svg>
        <Trans>Agents</Trans>
        <MaxBadge className="ml-auto" />
      </button>
      <button
        onClick={() => { navigate('/memory'); onNavigate?.(); }}
        className={`flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors ${
          isActive('/memory')
            ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-500/10 font-semibold'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5'
        }`}
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
        <Trans>Memory</Trans>
      </button>
      {isAdmin && (
        <button
          onClick={() => { navigate('/connections'); onNavigate?.(); }}
          className={`flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors ${
            isActive('/connections')
              ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-500/10 font-semibold'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5'
          }`}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
          </svg>
          <Trans>Connections</Trans>
          <MaxBadge className="ml-auto" />
        </button>
      )}
      <button
        onClick={() => { navigate('/private-mcp'); onNavigate?.(); }}
        className={`flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors ${
          isActive('/private-mcp')
            ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-500/10 font-semibold'
            : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5'
        }`}
      >
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.25 14.25h13.5m-13.5 0a3 3 0 01-3-3m3 3a3 3 0 100 6h13.5a3 3 0 100-6m-16.5-3a3 3 0 013-3h13.5a3 3 0 013 3m-19.5 0a4.5 4.5 0 01.9-2.7L5.737 5.1a3.375 3.375 0 012.7-1.35h7.126c1.062 0 2.062.5 2.7 1.35l2.587 3.45a4.5 4.5 0 01.9 2.7m0 3a3 3 0 01-3 3m0 3h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008zm-3 6h.008v.008h-.008v-.008zm0-6h.008v.008h-.008v-.008z" />
        </svg>
        <Trans>Private MCP</Trans>
        <MaxBadge className="ml-auto" />
      </button>
      {/* User Management is a Max-tier surface: Pro users never see it.
          The gate is on the COMPANY having Max (own seat counts too), not on
          the admin's personal seat alone: seats are blended, and the admin of
          a Max company often holds a Pro seat. Keying off their own seat would
          hide the very page where the company's Max seats get assigned. */}
      {isAdmin && !company?.isPersonal
        && (accountStatus?.effectiveTier === 'max' || (company?.licensedSeatsMax ?? 0) > 0) && (
        <button
          onClick={() => { navigate('/users'); onNavigate?.(); }}
          className={`flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors ${
            isActive('/users')
              ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-500/10 font-semibold'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5'
          }`}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
          </svg>
          <Trans>User Management</Trans>
        </button>
      )}
      {isAdmin && (
        <button
          onClick={() => { navigate('/api-keys'); onNavigate?.(); }}
          className={`flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors ${
            isActive('/api-keys')
              ? 'text-blue-600 dark:text-blue-400 bg-blue-50/50 dark:bg-blue-500/10 font-semibold'
              : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-white/5'
          }`}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
          </svg>
          <Trans>API Keys</Trans>
        </button>
      )}
      {/* EPIC-15: persistent upgrade entry for free accounts. Gold accent
          only — never a repaint. */}
      {!isMax && (
        <button
          onClick={() => { setUpgradeOpen(true); onNavigate?.(); }}
          className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors font-semibold"
          style={{ color: 'var(--color-max-text)', border: '1px solid var(--color-max-border)', backgroundColor: 'var(--color-max-bg)' }}
        >
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
          </svg>
          <Trans>Upgrade to Max</Trans>
        </button>
      )}
    </nav>
  );

  const accountPanel = () => (
    <div className="p-5 glass-panel rounded-2xl border border-blue-100 dark:border-blue-900/30">
      <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--color-text-muted)' }}><Trans>Account</Trans></p>
      <div className="space-y-1.5">
        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{user?.email}</p>
        {company && (
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            {company.name}
            {company.isPersonal && <span className="ml-1 opacity-60"><Trans>(Personal)</Trans></span>}
          </p>
        )}
        <p className="text-xs flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
          {isAdmin ? <Trans>Administrator</Trans> : <Trans>Company Member</Trans>}
          {accountStatus?.effectiveTier === 'max' ? (
            <MaxBadge />
          ) : accountStatus?.effectiveStatus === 'pro' ? (
            <span
              className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
              style={{ border: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}
            >
              Pro
            </span>
          ) : null}
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen transition-colors duration-200" style={{ backgroundColor: 'var(--color-bg)', color: 'var(--color-text)' }}>
      <div className="px-4 py-4 md:px-8 md:py-8">
        <div className="max-w-7xl mx-auto space-y-6 md:space-y-8">
          {/* Header */}
          <header className="flex justify-between items-center py-3 md:py-4 border-b" style={{ borderColor: 'var(--color-border)' }}>
            <div className="flex items-center gap-2 md:gap-3">
              <button
                className="md:hidden p-2 -ml-2 rounded-lg transition-colors hover:bg-slate-100 dark:hover:bg-white/10"
                onClick={() => setMobileMenuOpen(true)}
                aria-label={t`Open menu`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <img
                src={isDark ? "/privacypal-logo-light.png" : "/privacypal-logo.png"}
                alt="PrivacyPal"
                className="w-36 sm:w-48 md:w-64 h-auto prism-edge"
              />
            </div>
            <div className="flex gap-1.5 sm:gap-2 md:gap-3 items-center">
              <div className="hidden lg:block">
                <button
                  type="button"
                  onClick={() => setShowDownloadModal(true)}
                  title={t`Download PrivacyPal for desktop`}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '10px 20px', fontSize: '13px', fontWeight: 600,
                    border: 'none', borderRadius: '10px',
                    background: 'var(--color-text)', color: 'var(--color-surface)',
                    cursor: 'pointer', transition: 'all 200ms',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                  }}
                >
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <span><Trans>Download Now</Trans></span>
                </button>
              </div>
              {/* Invite button - hidden on mobile */}
              <button
                onClick={handleGenerateInvite}
                className="hidden md:flex p-2 rounded-lg transition-colors hover:bg-slate-100 dark:hover:bg-white/10"
                style={{ color: 'var(--color-text-muted)' }}
                title={t`Generate an invite link to share with team members`}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </button>
              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg transition-colors hover:bg-slate-100 dark:hover:bg-white/10"
                title={isDark ? t`Switch to light mode` : t`Switch to dark mode`}
              >
                {isDark ? (
                  <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'var(--color-text-secondary)' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>
              {/* Avatar */}
              <div className="w-8 h-8 rounded-full bg-slate-300 dark:bg-slate-600 flex items-center justify-center text-xs font-semibold text-slate-600 dark:text-slate-300">
                {user?.email?.charAt(0).toUpperCase() || 'U'}
              </div>
              {/* Sign out - desktop only */}
              <button
                className="hidden md:block px-4 py-2 text-sm font-medium border rounded-full transition-all hover:bg-white dark:hover:bg-white/10"
                style={{ borderColor: 'var(--color-border)' }}
                onClick={logout}
              >
                <Trans>Sign Out</Trans>
              </button>
            </div>
          </header>

          {/* Desktop Layout Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
            {/* Desktop Sidebar */}
            <aside className="hidden md:block md:col-span-3 space-y-6">
              {navItems()}
              {accountPanel()}
            </aside>

            <main className="col-span-1 md:col-span-9 space-y-6">
              {children}
            </main>
          </div>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="fixed inset-0 bg-black/50 animate-fade-in" onClick={() => setMobileMenuOpen(false)} />
          <div className="fixed left-0 top-0 h-full w-72 shadow-2xl overflow-y-auto animate-slide-in-left" style={{ backgroundColor: 'var(--color-surface)' }}>
            <div className="p-5 space-y-6">
              <div className="flex items-center justify-between">
                <img
                  src={isDark ? "/privacypal-logo-light.png" : "/privacypal-logo.png"}
                  alt="PrivacyPal"
                  className="w-36 h-auto"
                />
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="p-2 rounded-lg transition-colors hover:bg-slate-100 dark:hover:bg-white/10"
                  aria-label={t`Close menu`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {navItems(() => setMobileMenuOpen(false))}

              <div className="border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
                {accountPanel()}
              </div>

              <div className="space-y-2 border-t pt-4" style={{ borderColor: 'var(--color-border)' }}>
                <button
                  onClick={() => { handleGenerateInvite(); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors hover:bg-slate-50 dark:hover:bg-white/5"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  <Trans>Invite Team Members</Trans>
                </button>
                <button
                  type="button"
                  onClick={() => { setShowDownloadModal(true); setMobileMenuOpen(false); }}
                  className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors hover:bg-slate-50 dark:hover:bg-white/5"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  <Trans>Download Now</Trans>
                </button>
                <button
                  onClick={logout}
                  className="flex items-center gap-3 w-full text-left px-4 py-2.5 text-sm rounded-lg transition-colors text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10"
                >
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  <Trans>Sign Out</Trans>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="rounded-xl p-6 max-w-md w-full mx-4 shadow-xl" style={{ backgroundColor: 'var(--color-surface)' }}>
            <h2 className="text-xl font-bold mb-4"><Trans>Invite Link Generated</Trans></h2>
            <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>
              <Trans>Share this link with users you want to invite to your company:</Trans>
            </p>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={inviteLink}
                readOnly
                className="flex-1 px-4 py-2 rounded-lg text-sm"
                style={{ backgroundColor: 'var(--color-surface-secondary)', border: '1px solid var(--color-border)', color: 'var(--color-text)' }}
              />
              <button
                onClick={handleCopyInvite}
                className="p-2 transition-colors hover:opacity-80"
                style={{ color: 'var(--color-text-muted)' }}
                title={t`Copy to clipboard`}
              >
                {copySuccess ? (
                  <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                )}
              </button>
            </div>
            <button
              onClick={() => { setShowInviteModal(false); setInviteLink(''); }}
              className="w-full px-4 py-2 rounded-lg transition-colors"
              style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)' }}
            >
              <Trans>Close</Trans>
            </button>
          </div>
        </div>
      )}

      {/* Desktop download modal (aligned with privacypal-web desktop.html) */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div
            className="rounded-2xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto border"
            style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}
          >
            <div className="sticky top-0 z-10 flex justify-between items-center px-5 py-4 border-b rounded-t-2xl" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>
              <h2 className="text-lg sm:text-xl font-bold pr-2"><Trans>Download PrivacyPal</Trans></h2>
              <button
                type="button"
                onClick={() => setShowDownloadModal(false)}
                className="transition-colors hover:opacity-80 shrink-0"
                style={{ color: 'var(--color-text-muted)' }}
                aria-label={t`Close`}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="p-5 sm:p-6 space-y-6">
              <div className="inline-flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-full border text-sm font-semibold" style={{ backgroundColor: 'var(--color-surface-secondary)', borderColor: 'var(--color-border)', color: 'var(--color-text)' }}>
                <span><Trans>PrivacyPal for desktop</Trans></span>
              </div>
              {/* The version badge used to live HERE, showing
                  DESKTOP_VERSION_WINDOWS for the whole modal, which labelled
                  the macOS and Linux cards with a version they were not. That
                  contradicted the comment on the pins themselves ("each link
                  carries the version that platform actually shipped") and the
                  Linux comment, which says in as many words that Linux is cut
                  by hand and routinely lags. Each card carries its own badge
                  now, so a lagging platform is visibly lagging instead of
                  silently mislabelled. */}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                {/* Windows */}
                <article className="rounded-3xl border p-6 sm:p-7 relative overflow-hidden bg-gradient-to-br from-sky-50 to-slate-100 dark:from-slate-800/80 dark:to-slate-900/90" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="relative z-[1] space-y-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="grid grid-cols-2 gap-0.5 w-7 h-7" aria-hidden>
                          <span className="rounded-sm bg-[#f65314]" />
                          <span className="rounded-sm bg-[#7cbb00]" />
                          <span className="rounded-sm bg-[#00a4ef]" />
                          <span className="rounded-sm bg-[#ffbb00]" />
                        </div>
                        <span className="text-[11px] font-bold tracking-wider uppercase" style={{ color: 'var(--color-text-muted)' }}>Microsoft</span>
                      </div>
                      <svg className="w-11 h-11 shrink-0 text-[#0078d4]" viewBox="0 0 24 24" aria-hidden>
                        <path fill="currentColor" d="M3 4.5L10.5 3.5V11H3V4.5ZM11.5 3.35L21 2V11H11.5V3.35ZM3 12H10.5V20.5L3 19.5V12ZM11.5 12H21V22L11.5 20.65V12Z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold font-serif text-slate-900 dark:text-slate-100 mb-2 flex items-center gap-2.5">Windows 11<span className="tabular-nums px-2.5 py-0.5 rounded-full text-xs font-bold border align-middle" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>v{DESKTOP_VERSION_WINDOWS}</span></h3>
                      <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                        <Trans>Run PrivacyPal on your PC with a full installer. Optimized for the Windows 11 experience.</Trans>
                      </p>
                    </div>
                    <a
                      href={DOWNLOAD_WINDOWS_EXE}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center justify-center gap-3 rounded-[14px] py-4 px-5 font-bold text-white bg-[#0078d4] hover:bg-[#006cbd] transition-all shadow-lg shadow-sky-500/25"
                    >
                      <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <path d="M3 4.5L10.5 3.5V11H3V4.5ZM11.5 3.35L21 2V11H11.5V3.35ZM3 12H10.5V20.5L3 19.5V12ZM11.5 12H21V22L11.5 20.65V12Z" />
                      </svg>
                      <Trans>Download for Windows</Trans>
                    </a>
                    <p className="text-xs font-mono break-all" style={{ color: 'var(--color-text-muted)' }}>
                      PrivacyPal-Setup-{DESKTOP_VERSION_WINDOWS}.exe
                    </p>
                    <div className="pt-5 border-t border-black/10 dark:border-white/10">
                      <p className="text-[11px] font-bold uppercase tracking-wide mb-2.5 flex items-center gap-2" style={{ color: 'var(--color-text-muted)' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="4 17 10 11 4 5" /><line x1="12" y1="19" x2="20" y2="19" /></svg>
                        <Trans>Or install via command line</Trans>
                      </p>
                      <div className="flex items-center gap-2 rounded-[10px] border px-3 py-2.5 bg-slate-100/90 dark:bg-slate-950/50" style={{ borderColor: 'var(--color-border)' }}>
                        <span className="text-[10px] font-semibold uppercase tracking-wide shrink-0" style={{ color: 'var(--color-text-muted)' }}>PowerShell</span>
                        <code className="flex-1 min-w-0 text-xs font-mono truncate text-slate-700 dark:text-slate-300">
                          <span className="text-slate-400 select-none">&gt; </span>{CLI_WINGET}
                        </code>
                        <button
                          type="button"
                          onClick={() => copyCliCommand(CLI_WINGET)}
                          className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-md border bg-white dark:bg-slate-800 hover:opacity-90 transition-opacity"
                          style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                        >
                          <Trans>Copy</Trans>
                        </button>
                      </div>
                    </div>
                  </div>
                </article>

                {/* macOS */}
                <article className="rounded-3xl border p-6 sm:p-7 relative overflow-hidden bg-gradient-to-br from-neutral-100 to-neutral-200/80 dark:from-slate-800/80 dark:to-slate-900/90" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="relative z-[1] space-y-5">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div className="flex items-center gap-3.5">
                        <svg className="w-10 h-10 text-neutral-900 dark:text-neutral-100 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                        </svg>
                        <span className="text-[11px] font-bold tracking-wider uppercase" style={{ color: 'var(--color-text-muted)' }}>Apple</span>
                      </div>
                      <span className="text-xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">macOS</span>
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold font-serif text-slate-900 dark:text-slate-100 mb-2 flex items-center gap-2.5">macOS<span className="tabular-nums px-2.5 py-0.5 rounded-full text-xs font-bold border align-middle" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>v{DESKTOP_VERSION_MACOS}</span></h3>
                      <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                        <Trans>Native builds for Apple Silicon and Intel Macs. Pick the one that matches your chip.</Trans>
                      </p>
                    </div>
                    <a
                      href={DOWNLOAD_MACOS_ARM64}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center justify-center gap-3 rounded-[14px] py-4 px-5 font-bold text-white bg-neutral-900 hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white transition-all shadow-lg shadow-neutral-500/25"
                    >
                      <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                      </svg>
                      <Trans>Download for Apple Silicon</Trans>
                    </a>
                    <p className="text-xs font-mono break-all" style={{ color: 'var(--color-text-muted)' }}>
                      PrivacyPal-{DESKTOP_VERSION_MACOS}-arm64.dmg
                    </p>
                    <p className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                      <Trans>Apple Silicon only (M1 and later). Apple menu → About This Mac to check.</Trans>
                    </p>
                  </div>
                </article>
              </div>

              {/* Linux. Deliberately NOT a third column beside Windows and macOS:
                  those two are the same product in two wrappers, and this is a
                  different one. No GUI exists for Linux, so presenting it as a
                  peer download would promise a desktop app that does not and
                  will not ship. It sits below, labelled for what it is. */}
              <article className="rounded-3xl border p-6 sm:p-7 relative overflow-hidden bg-gradient-to-br from-neutral-100 to-neutral-200/80 dark:from-slate-800/80 dark:to-slate-900/90" style={{ borderColor: 'var(--color-border)' }}>
                <div className="relative z-[1] space-y-5">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3.5">
                      <svg className="w-10 h-10 text-neutral-900 dark:text-neutral-100 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <rect x="2.5" y="3.5" width="19" height="17" rx="2.5" />
                        <path d="M6.5 9l3 3-3 3M12.5 15.5h5" />
                      </svg>
                      <span className="text-[11px] font-bold tracking-wider uppercase" style={{ color: 'var(--color-text-muted)' }}>Server</span>
                    </div>
                    <span className="text-xl font-bold tracking-tight text-neutral-900 dark:text-neutral-100">Linux</span>
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="text-2xl font-bold font-serif text-slate-900 dark:text-slate-100 flex items-center gap-2.5"><Trans>Linux agent shield</Trans><span className="tabular-nums px-2.5 py-0.5 rounded-full text-xs font-bold border align-middle" style={{ backgroundColor: 'var(--color-surface)', borderColor: 'var(--color-border)' }}>v{DESKTOP_VERSION_LINUX}</span></h3>
                      <span className="text-[10px] font-bold tracking-wider uppercase px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">Max</span>
                    </div>
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
                      <Trans>Protects command-line AI agents on Ubuntu, Fedora and WSL. This is a headless CLI, not a desktop app: it covers agents such as Claude Code and Codex, and does not cover browser AI.</Trans>
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <a
                      href={DOWNLOAD_LINUX_X86_64}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center justify-center gap-3 rounded-[14px] py-4 px-5 font-bold text-white bg-neutral-900 hover:bg-neutral-800 dark:bg-neutral-100 dark:text-neutral-900 dark:hover:bg-white transition-all shadow-lg shadow-neutral-500/25"
                    >
                      <Trans>Download for x86_64</Trans>
                    </a>
                    <a
                      href={DOWNLOAD_LINUX_AARCH64}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex w-full items-center justify-center gap-3 rounded-[14px] py-4 px-5 font-bold border bg-white dark:bg-slate-800 hover:opacity-90 transition-opacity text-slate-900 dark:text-slate-100"
                      style={{ borderColor: 'var(--color-border)' }}
                    >
                      <Trans>Download for ARM64</Trans>
                    </a>
                  </div>
                  <p className="text-xs font-mono break-all" style={{ color: 'var(--color-text-muted)' }}>
                    privacypal-agent-{DESKTOP_VERSION_LINUX}-linux-&lt;arch&gt;.tar.gz
                  </p>
                  <div className="flex items-center gap-2 rounded-xl border px-3 py-2" style={{ borderColor: 'var(--color-border)', backgroundColor: 'var(--color-surface)' }}>
                    <code className="text-[12px] font-mono flex-1 overflow-x-auto whitespace-nowrap" style={{ color: 'var(--color-text-secondary)' }}>
                      <span className="text-slate-400 select-none">&gt; </span>{CLI_LINUX_INSTALL}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyCliCommand(CLI_LINUX_INSTALL)}
                      className="shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-md border bg-white dark:bg-slate-800 hover:opacity-90 transition-opacity"
                      style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
                    >
                      <Trans>Copy</Trans>
                    </button>
                  </div>
                  <p className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                    <Trans>Requires a Max account. Run uname -m to check your architecture.</Trans>
                  </p>
                </div>
              </article>

              <div className="rounded-2xl border p-5 sm:p-6" style={{ backgroundColor: 'var(--color-surface-secondary)', borderColor: 'var(--color-border)' }}>
                <h4 className="text-base font-bold font-serif mb-3 text-slate-900 dark:text-slate-100"><Trans>Before you install</Trans></h4>
                <ul className="space-y-2.5 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                  <li className="pl-6 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-blue-600/80">
                    <Trans>Close other AI apps if the installer prompts you. PrivacyPal hooks in at the system edge for full coverage.</Trans>
                  </li>
                  <li className="pl-6 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-blue-600/80">
                    <Trans>Windows: run the installer as a user with permission to install applications; SmartScreen may ask you to confirm the publisher.</Trans>
                  </li>
                  <li className="pl-6 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-blue-600/80">
                    <Trans>macOS: open the DMG, drag PrivacyPal to Applications, then launch from Applications. Gatekeeper may ask you to confirm the first launch.</Trans>
                  </li>
                  <li className="pl-6 relative before:content-[''] before:absolute before:left-0 before:top-2 before:w-2 before:h-2 before:rounded-full before:bg-blue-600/80">
                    <Trans>Linux: install.sh needs no root and installs into your home directory. Run privacypal login once, then privacypal status to confirm which agents are covered.</Trans>
                  </li>
                </ul>
              </div>

              <div className="flex justify-end pt-1">
                <button
                  type="button"
                  onClick={() => setShowDownloadModal(false)}
                  className="px-6 py-2.5 rounded-lg transition-colors font-medium text-sm"
                  style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text)' }}
                >
                  <Trans>Close</Trans>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EPIC-15: shared Max checkout, reachable from the invite flow and the
          nav upgrade entry. */}
      <UpgradeModal open={upgradeOpen} onClose={() => setUpgradeOpen(false)} source="layout" />
    </div>
  );
}
